/**
 * supabaseSync.ts — DB-Persistenz für den Wizard-Store (AP11b).
 *
 * Zweck:
 *   Sensible Wizard-Felder (BWA, BG, Health, Private) NICHT mehr nur in
 *   localStorage, sondern verschlüsselt in Supabase persistieren — RLS
 *   trennt nach Owner/Tenant, libsodium-Application-Layer-Crypto greift VOR
 *   der RLS-Policy (Defense-in-Depth).
 *
 * Datenmodell-Mapping (siehe supabase/migrations/0001_init.sql):
 *
 *   wizard.cases[caseId].triage          → dimension_data WHERE dimension='identity'
 *                                           (klartext in structured_data.triage —
 *                                            Triage ist nicht sensibel; nur legalBasis)
 *
 *   wizard.cases[caseId].dimensionData[D]:
 *     - status, lastEditedAt           → dimension_data.structured_data.meta
 *     - fields (Stub-Texte)            → dimension_data.structured_data.fields
 *                                          (für 'health' und 'private' VERSCHLÜSSELT
 *                                           in dimension_data.sensitive_payload)
 *
 *   wizard.cases[caseId].resources     → dimension_data WHERE dimension='resources'
 *                                          (Aggregat-Cents im Klartext für Score-Engine)
 *                                       + financial_dataset (BWA + BG)
 *                                          (BG-Komposition VERSCHLÜSSELT in
 *                                           raw_bwa_payload, Aggregat in Klartext)
 *
 *   wizard.cases[caseId].assessments   → dimension_data.structured_data.assessments
 *                                          (am Identity-Row angehängt — pragmatisch,
 *                                           Bewertungstexte sind nicht Art-9, aber
 *                                           gehören zum Case-Kontext)
 *
 * Konflikt-Strategie (lokal vs. DB):
 *   Last-write-wins per `lastSavedAt`. Beim ersten loadWizardCase nach Mount
 *   gewinnt der DB-Stand, falls neuer; sonst bleibt der lokale Stand
 *   (Offline-Edit hat Vorrang). Im häufigen Fall (Single-Device) gibt es
 *   keinen Konflikt — der lokale Stand wird beim ersten Save in DB
 *   überschrieben (Migrationspfad: lokal → DB).
 *
 * Debounce: Der Aufrufer ruft saveWizardCase via debounced wrapper, damit
 * jedes Tipper-Event keinen DB-Roundtrip macht (Default 1.5 s). Default
 * exportiert: createDebouncedSaver(...).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from '../supabase';
import type {
  Database,
  DimensionDataRow,
  DimensionLayer,
  FinancialDatasetRow,
} from '../supabaseTypes';
import type { DimensionKey } from '../../components/hub/dimensions';
import { encryptField, decryptField, isEncryptedAvailable } from '../crypto';
import {
  makeEmptyDimensions,
  type WizardCaseState,
  type AssessmentDraft,
  type DimensionDataDraft,
  type ResourcesDraft,
  type ResourcesBgMemberDraft,
  type ResourcesKduDraft,
  type TriageDraft,
} from './wizard';

type SupaClient = SupabaseClient<Database>;
type DimensionDataInsert = Database['public']['Tables']['dimension_data']['Insert'];
type FinancialDatasetInsert = Database['public']['Tables']['financial_dataset']['Insert'];

/** Dimensionen, deren Freitext-Felder als Art-9-sensibel klassifiziert sind. */
const SENSITIVE_DIMENSIONS: ReadonlySet<DimensionKey> = new Set(['health', 'private']);

// -----------------------------------------------------------------------------
// Krypto-Scope (Case-Key, Phase 4)
//
// Sensible Payloads werden mit einem CASE-Key (abgeleitet aus caseId)
// verschlüsselt — dann können BEIDE Fall-Teilnehmer (Mandant + Berater)
// entschlüsseln. Kennzeichnung in sensitive_key_id = 'case:<caseId8>'.
// Legacy-Payloads ('kdf:<userId8>' oder ohne Kennzeichen) werden weiter mit
// dem User-Key des ursprünglichen Erfassers gelesen (Fallback, kein
// Datenverlust). Siehe docs/Zwei-Ebenen-Go-Live.md.
// -----------------------------------------------------------------------------
function caseKeyId(caseId: string): string {
  return `case:${caseId.slice(0, 8)}`;
}

function decryptScope(caseId: string, userId: string, sensitiveKeyId: string | null): string {
  return sensitiveKeyId?.startsWith('case:') === true ? caseId : userId;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Lädt den Wizard-State eines Cases aus der DB. Liefert null, wenn kein
 * Eintrag existiert oder Crypto nicht verfügbar ist (Letzteres ist ein
 * Hard-Block — UI soll das vorher abfangen).
 *
 * @param caseId UUID des Cases (RLS sorgt für Sichtbarkeit)
 * @param userId User-ID für KDF-Key-Ableitung
 */
export async function loadWizardCase(
  caseId: string,
  userId: string,
  client?: SupaClient,
): Promise<WizardCaseState | null> {
  if (!isEncryptedAvailable()) {
    throw new Error('loadWizardCase: crypto not initialized — call ensureCryptoReady() first.');
  }
  const supa = client ?? getSupabaseClient();

  const [dimRes, finRes] = await Promise.all([
    supa.from('dimension_data').select('*').eq('case_id', caseId).is('deleted_at', null),
    supa
      .from('financial_dataset')
      .select('*')
      .eq('case_id', caseId)
      .eq('layer', 'mandant')
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })
      .limit(1),
  ]);

  if (dimRes.error) throw new Error(`loadWizardCase: dimension_data: ${dimRes.error.message}`);
  if (finRes.error) throw new Error(`loadWizardCase: financial_dataset: ${finRes.error.message}`);

  // Cast: supabase-js mit unserem hand-gepflegten Database-Type liefert hier
  // ein zu enges `never`-Result, weil die Insert/Update-Generics streng sind.
  // Die Rows sind beim SELECT garantiert <Row>-shaped — wir asserten das.
  const dimRows = (dimRes.data ?? []) as DimensionDataRow[];
  const finRow = (finRes.data?.[0] ?? null) as FinancialDatasetRow | null;
  if (dimRows.length === 0 && finRow === null) return null;

  // Reconstruct WizardCaseState aus den Rows. Wir arbeiten lokal auf einer
  // mutablen Kopie (WritableCaseState), bevor wir am Ende den read-only
  // WizardCaseState zurückgeben.
  const state = makeEmptyCaseState(caseId) as WritableCaseState;
  let latestSaved: string | null = null;

  for (const row of dimRows) {
    if (row.updated_at && (!latestSaved || row.updated_at > latestSaved)) {
      latestSaved = row.updated_at;
    }
    await applyDimensionRow(state, row, userId);
  }

  if (finRow) {
    if (finRow.updated_at && (!latestSaved || finRow.updated_at > latestSaved)) {
      latestSaved = finRow.updated_at;
    }
    await applyFinancialRow(state, finRow, userId);
  }

  return { ...(state as WizardCaseState), lastSavedAt: latestSaved };
}

/** Schreibbares Spiegelbild von WizardCaseState — nur intern verwendet. */
type WritableCaseState = {
  -readonly [K in keyof WizardCaseState]: WizardCaseState[K] extends ReadonlyArray<infer U>
    ? U[]
    : WizardCaseState[K];
};

/**
 * Persistiert den Wizard-State in die DB. Schreibt pro Dimension einen
 * UPSERT in dimension_data + einen UPSERT in financial_dataset (für die
 * Resources-Daten). Sensible Felder werden vorher per User-Key verschlüsselt.
 *
 * Idempotent: Bei mehrfachem Aufruf für identischen State werden die Rows
 * mit gleichen Werten überschrieben — Audit-Trigger schreibt entsprechend
 * UPDATE-Einträge mit before/after.
 */
export async function saveWizardCase(
  caseState: WizardCaseState,
  userId: string,
  layer: DimensionLayer,
  client?: SupaClient,
): Promise<void> {
  if (!isEncryptedAvailable()) {
    throw new Error('saveWizardCase: crypto not initialized — call ensureCryptoReady() first.');
  }
  const supa = client ?? getSupabaseClient();
  void userId; // Krypto nutzt jetzt den Case-Key; userId nur noch für Legacy-Reads.

  // 1. dimension_data: ein UPSERT pro Dimension IN DER ÜBERGEBENEN EBENE.
  //    Mandant-Mount schreibt layer='mandant', Berater-Mount layer='berater'.
  const dimensionUpserts: DimensionDataInsert[] = [];
  const dimKeys: readonly DimensionKey[] = [
    'identity',
    'activity',
    'style',
    'resources',
    'health',
    'private',
  ];

  for (const dim of dimKeys) {
    const row = await buildDimensionInsert(caseState, dim, layer);
    if (row) dimensionUpserts.push(row);
  }

  // 2. financial_dataset für Resources — nur auf der Mandant-Ebene (Erfassung).
  const finInsert = layer === 'mandant' ? await buildFinancialInsert(caseState) : null;

  // Ein einziger upsert-Call pro Tabelle (Batch).
  if (dimensionUpserts.length > 0) {
    const { error } = await supa
      .from('dimension_data')
      .upsert(dimensionUpserts as never, { onConflict: 'case_id,dimension,layer' });
    if (error) throw new Error(`saveWizardCase: dimension_data upsert: ${error.message}`);
  }

  if (finInsert) {
    // financial_dataset hat KEIN unique on case_id (Versionen erlaubt). Wir
    // pflegen aber genau einen aktiven Datensatz pro Case — also: erst
    // existierenden updaten, sonst insert.
    const { data: existing, error: selErr } = await supa
      .from('financial_dataset')
      .select('id')
      .eq('case_id', caseState.caseId)
      .eq('layer', 'mandant')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (selErr) throw new Error(`saveWizardCase: financial_dataset select: ${selErr.message}`);

    const existingRow = existing as { id: string } | null;
    if (existingRow?.id) {
      const { error: updErr } = await supa
        .from('financial_dataset')
        .update(finInsert as never)
        .eq('id', existingRow.id);
      if (updErr) throw new Error(`saveWizardCase: financial_dataset update: ${updErr.message}`);
    } else {
      const { error: insErr } = await supa.from('financial_dataset').insert(finInsert as never);
      if (insErr) throw new Error(`saveWizardCase: financial_dataset insert: ${insErr.message}`);
    }
  }
}

/**
 * Erzeugt einen debounced Saver — typisch 1.5s nach letzter Änderung. Gibt
 * eine `schedule()`-Funktion zurück, die der Wizard-Store nach jeder
 * Mutation aufruft. Letzter Aufruf gewinnt; Errors werden via onError
 * gemeldet, damit das UI eine Fehler-Toast zeigen kann.
 */
export interface DebouncedSaverOptions {
  readonly userId: string;
  /** Ebene, in die dieser Mount schreibt (Mandant-Mount vs. Berater-Mount). */
  readonly layer: DimensionLayer;
  readonly delayMs?: number;
  readonly onError?: (err: unknown) => void;
  readonly onSaved?: () => void;
  readonly client?: SupaClient;
}

export interface DebouncedSaver {
  schedule: (caseState: WizardCaseState) => void;
  flush: () => Promise<void>;
  cancel: () => void;
}

export function createDebouncedSaver(opts: DebouncedSaverOptions): DebouncedSaver {
  const delay = opts.delayMs ?? 1500;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: WizardCaseState | null = null;

  async function runNow(): Promise<void> {
    if (!pending) return;
    const snapshot = pending;
    pending = null;
    try {
      await saveWizardCase(snapshot, opts.userId, opts.layer, opts.client);
      opts.onSaved?.();
    } catch (err) {
      opts.onError?.(err);
    }
  }

  return {
    schedule(caseState) {
      pending = caseState;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void runNow();
      }, delay);
    },
    async flush() {
      if (timer) clearTimeout(timer);
      timer = null;
      await runNow();
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}

// =============================================================================
// INTERNAL — Mapping wizard ↔ DB rows
// =============================================================================

function makeEmptyCaseState(caseId: string): WizardCaseState {
  return {
    caseId,
    currentStep: 'triage',
    triage: { status: 'untouched', lastEditedAt: null, legalBasis: null },
    dimensionData: makeEmptyDimensions(),
    // Berater-Ebene wird von der DB-Sync noch nicht befüllt (Phase 2 DB-Teil);
    // replaceCaseFromDb bewahrt eine bestehende Berater-Ebene im Store.
    beraterDimensionData: makeEmptyDimensions(),
    resources: {
      status: 'untouched',
      lastEditedAt: null,
      activeSubStep: 'bwa',
      revenueCents: null,
      costsCents: null,
      hoursPerMonth: null,
      liquidityCents: null,
      burnRateCents: null,
      shortTermLiabilitiesCents: null,
      bgComposition: [],
      kdu: { kaltCents: 0, nebenCents: 0, heizCents: 0 },
      bruttoErwerbseinkommenCents: null,
      sonstigesEinkommenCents: null,
    },
    assessments: makeEmptyAssessmentsLocal(),
    lastSavedAt: null,
  };
}

function makeEmptyAssessmentsLocal(): AssessmentDraft {
  return {
    lastEditedAt: null,
    revenueAssessment: '',
    costsAssessment: '',
    profitAssessment: '',
    hourlyRateAssessment: '',
    liquidityAssessment: '',
    runwayAssessment: '',
    utilizationAssessment: '',
    gapInterpretation: '',
    verdictLongParagraph: '',
    verdictReasoning: '',
    recommendationDetailed: '',
    conditionsParagraph: '',
    reevaluationProposal: '',
    constraintDiscussion: '',
    riskIndicators: '',
    strengthIndicators: '',
    dimensionTexts: {
      identity: { short: '', detail: '' },
      activity: { short: '', detail: '' },
      style: { short: '', detail: '' },
      resources: { short: '', detail: '' },
      health: { short: '', detail: '' },
      private: { short: '', detail: '' },
    },
  };
}

interface DimensionStructured {
  meta?: { status?: DimensionDataDraft['status']; lastEditedAt?: string | null };
  fields?: Readonly<Record<string, string>>;
  triage?: TriageDraft;
  assessments?: AssessmentDraft;
  resources?: ResourcesPersistShape;
}

interface ResourcesPersistShape {
  status: ResourcesDraft['status'];
  lastEditedAt: string | null;
  activeSubStep: ResourcesDraft['activeSubStep'];
  revenueCents: number | null;
  costsCents: number | null;
  hoursPerMonth: number | null;
  liquidityCents: number | null;
  burnRateCents: number | null;
  shortTermLiabilitiesCents: number | null;
  kdu: ResourcesKduDraft;
  bruttoErwerbseinkommenCents: number | null;
  sonstigesEinkommenCents: number | null;
  // bgComposition wird verschlüsselt in financial_dataset.raw_bwa_payload
  // abgelegt — siehe buildFinancialInsert.
}

async function buildDimensionInsert(
  state: WizardCaseState,
  dim: DimensionKey,
  layer: DimensionLayer,
): Promise<DimensionDataInsert | null> {
  // Ebenen-Routing: Mandant-Ebene = dimensionData; Berater-Ebene =
  // beraterDimensionData. Triage + Resources hängen an der Mandant-Ebene,
  // Assessments (Stellungnahme-Texte) an der Berater-Ebene.
  const dimDraft = layer === 'mandant' ? state.dimensionData[dim] : state.beraterDimensionData[dim];
  const hasContent =
    dimDraft.status !== 'untouched' ||
    Object.keys(dimDraft.fields).length > 0 ||
    dimDraft.reviewedAt !== null ||
    (dim === 'identity' && layer === 'mandant' && state.triage.status !== 'untouched') ||
    (dim === 'identity' && layer === 'berater' && hasAnyAssessment(state.assessments)) ||
    (dim === 'resources' && layer === 'mandant' && state.resources.status !== 'untouched');

  if (!hasContent) return null;

  const structured: DimensionStructured = {
    meta: { status: dimDraft.status, lastEditedAt: dimDraft.lastEditedAt },
  };

  if (dim === 'identity' && layer === 'mandant') {
    structured.triage = state.triage;
  }
  if (dim === 'identity' && layer === 'berater') {
    structured.assessments = state.assessments;
  }

  if (dim === 'resources' && layer === 'mandant') {
    structured.resources = {
      status: state.resources.status,
      lastEditedAt: state.resources.lastEditedAt,
      activeSubStep: state.resources.activeSubStep,
      revenueCents: state.resources.revenueCents,
      costsCents: state.resources.costsCents,
      hoursPerMonth: state.resources.hoursPerMonth,
      liquidityCents: state.resources.liquidityCents,
      burnRateCents: state.resources.burnRateCents,
      shortTermLiabilitiesCents: state.resources.shortTermLiabilitiesCents,
      kdu: state.resources.kdu,
      bruttoErwerbseinkommenCents: state.resources.bruttoErwerbseinkommenCents,
      sonstigesEinkommenCents: state.resources.sonstigesEinkommenCents,
    };
  }

  // Sensible Dimensionen (health, private): die fields-Map wandert in den
  // mit dem CASE-Key verschlüsselten sensitive_payload, NICHT in structured_data.
  let sensitivePayload: string | null = null;
  let sensitiveKeyId: string | null = null;
  if (SENSITIVE_DIMENSIONS.has(dim) && Object.keys(dimDraft.fields).length > 0) {
    const cipher = await encryptField(JSON.stringify(dimDraft.fields), state.caseId);
    sensitivePayload = bytesToBase64(cipher);
    sensitiveKeyId = caseKeyId(state.caseId);
  } else {
    structured.fields = dimDraft.fields;
  }

  return {
    case_id: state.caseId,
    dimension: dim,
    layer,
    structured_data: structured as unknown as Record<string, unknown>,
    free_text: null,
    sensitive_payload: sensitivePayload,
    sensitive_key_id: sensitiveKeyId,
    computed_score: null,
    confidence_pct: null,
    reviewed_at: layer === 'berater' ? dimDraft.reviewedAt : null,
  };
}

async function applyDimensionRow(
  state: WritableCaseState,
  row: DimensionDataRow,
  userId: string,
): Promise<void> {
  const dim = row.dimension;
  const layer = row.layer;
  const structured = (row.structured_data as DimensionStructured | null) ?? {};

  // Triage hängt an der Mandant-Ebene, Assessments an der Berater-Ebene.
  if (dim === 'identity' && layer === 'mandant' && structured.triage) {
    state.triage = structured.triage;
  }
  if (dim === 'identity' && layer === 'berater' && structured.assessments) {
    state.assessments = structured.assessments;
  }

  if (dim === 'resources' && layer === 'mandant' && structured.resources) {
    const r = structured.resources;
    state.resources = {
      ...state.resources,
      status: r.status,
      lastEditedAt: r.lastEditedAt,
      activeSubStep: r.activeSubStep,
      revenueCents: r.revenueCents,
      costsCents: r.costsCents,
      hoursPerMonth: r.hoursPerMonth,
      liquidityCents: r.liquidityCents,
      burnRateCents: r.burnRateCents,
      // ?? null: alte DB-Rows vor 0032 tragen diese Felder noch nicht.
      shortTermLiabilitiesCents: r.shortTermLiabilitiesCents ?? null,
      kdu: r.kdu,
      bruttoErwerbseinkommenCents: r.bruttoErwerbseinkommenCents ?? null,
      sonstigesEinkommenCents: r.sonstigesEinkommenCents ?? null,
    };
  }

  // Felder rekonstruieren — entweder Klartext oder entschlüsselt.
  let fields: Readonly<Record<string, string>> = structured.fields ?? {};
  if (SENSITIVE_DIMENSIONS.has(dim) && row.sensitive_payload) {
    try {
      const scope = decryptScope(row.case_id, userId, row.sensitive_key_id);
      const cipher = base64ToBytes(extractBase64FromPostgrestBytea(row.sensitive_payload));
      const plain = await decryptField(cipher, scope);
      fields = JSON.parse(plain) as Record<string, string>;
    } catch (err) {
      console.warn(`[supabaseSync] decrypt failed for dimension=${dim} layer=${layer}:`, err);
      fields = {};
    }
  }

  const draft: DimensionDataDraft = {
    status: structured.meta?.status ?? 'in-progress',
    lastEditedAt: structured.meta?.lastEditedAt ?? row.updated_at,
    fields,
    reviewedAt: layer === 'berater' ? row.reviewed_at : null,
  };
  if (layer === 'mandant') {
    state.dimensionData = { ...state.dimensionData, [dim]: draft };
  } else {
    state.beraterDimensionData = { ...state.beraterDimensionData, [dim]: draft };
  }
}

interface FinancialEncryptedShape {
  bgComposition: readonly ResourcesBgMemberDraft[];
}

async function buildFinancialInsert(
  state: WizardCaseState,
): Promise<FinancialDatasetInsert | null> {
  const r = state.resources;
  const hasResources =
    r.revenueCents !== null ||
    r.costsCents !== null ||
    r.hoursPerMonth !== null ||
    r.liquidityCents !== null ||
    r.bgComposition.length > 0;
  if (!hasResources) return null;

  const profitCents =
    r.revenueCents !== null && r.costsCents !== null ? r.revenueCents - r.costsCents : null;
  const hourlyRateCents =
    r.revenueCents !== null && r.costsCents !== null && r.hoursPerMonth && r.hoursPerMonth > 0
      ? Math.round((r.revenueCents - r.costsCents) / r.hoursPerMonth)
      : null;

  // BG-Komposition + KdU-Aggregat in Klartext (für Score-Engine), aber
  // die personenbezogene BG-Liste zusätzlich verschlüsselt als Roh-Payload.
  const bgClear = {
    member_count: r.bgComposition.length,
    kdu_cent: r.kdu.kaltCents + r.kdu.nebenCents + r.kdu.heizCents,
  };

  const sensitive: FinancialEncryptedShape = { bgComposition: r.bgComposition };
  const sensitiveCipher = await encryptField(JSON.stringify(sensitive), state.caseId);

  // Periode: pragmatisch heute (BWA-Monat würde sauberer sein, kommt in AP12).
  const today = new Date().toISOString().slice(0, 10);

  return {
    case_id: state.caseId,
    // Resources sind Mandanten-Erfassung → Mandant-Ebene.
    layer: 'mandant',
    period_start: today,
    period_end: today,
    revenue_monthly_cent: r.revenueCents,
    costs_monthly_cent: r.costsCents,
    profit_monthly_cent: profitCents,
    liquidity_cent: r.liquidityCents,
    hours_monthly: r.hoursPerMonth,
    hourly_rate_net_cent: hourlyRateCents,
    utilization_pct: null,
    runway_weeks: null,
    // 0032: § 11-Anrechnung + echte Burn-Rate (denormalisierte Kopie; die
    // maßgebliche Reload-Quelle ist dimension_data.structured_data.resources).
    brutto_erwerbseinkommen_cent: r.bruttoErwerbseinkommenCents,
    sonstiges_einkommen_cent: r.sonstigesEinkommenCents,
    monthly_burn_rate_cent: r.burnRateCents,
    bg_composition: bgClear,
    bg_total_need_cent: null,
    bg_uncovered_cent: null,
    tragfaehigkeitsluecke_cent: null,
    raw_bwa_storage_path: null,
    raw_eks_storage_path: null,
    raw_bwa_payload: bytesToBase64(sensitiveCipher),
    sensitive_key_id: caseKeyId(state.caseId),
  };
}

async function applyFinancialRow(
  state: WritableCaseState,
  row: FinancialDatasetRow,
  userId: string,
): Promise<void> {
  // Aggregate übernehmen
  state.resources = {
    ...state.resources,
    revenueCents: row.revenue_monthly_cent,
    costsCents: row.costs_monthly_cent,
    hoursPerMonth: row.hours_monthly,
    liquidityCents: row.liquidity_cent,
    // burnRateCents bleibt was im Store war — wird über setResourcesField separat persistiert.
  };

  // BG-Komposition aus verschlüsseltem Payload rekonstruieren.
  if (row.raw_bwa_payload) {
    try {
      const scope = decryptScope(row.case_id, userId, row.sensitive_key_id);
      const cipher = base64ToBytes(extractBase64FromPostgrestBytea(row.raw_bwa_payload));
      const plain = await decryptField(cipher, scope);
      const parsed = JSON.parse(plain) as FinancialEncryptedShape;
      if (Array.isArray(parsed.bgComposition)) {
        state.resources = { ...state.resources, bgComposition: parsed.bgComposition };
      }
    } catch (err) {
      console.warn('[supabaseSync] BG decrypt failed:', err);
    }
  }
}

function hasAnyAssessment(a: AssessmentDraft): boolean {
  if (a.lastEditedAt) return true;
  // schneller Heuristik-Check: irgendein Top-Level-Feld nicht leer?
  for (const k of Object.keys(a) as (keyof AssessmentDraft)[]) {
    if (k === 'lastEditedAt' || k === 'dimensionTexts') continue;
    const v = (a as unknown as Record<string, unknown>)[k];
    if (typeof v === 'string' && v.length > 0) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// bytea-Encoding-Helper
// -----------------------------------------------------------------------------
// Wir lagern Ciphertext als base64-String in bytea-Spalten ab. Postgrest
// liefert beim SELECT `\x<hex of base64-bytes>` — wir konvertieren zurück.
//
// Das Doppel-Encoding ist eine Konzession an supabase-js: ein direkter
// Uint8Array-Insert via REST geht nicht ohne PG-Side decode(). Diese
// Indirection kostet ~33% Storage, ist für unsere Payload-Größen (< 50 KiB)
// vertretbar. Plan: in AP15 auf Edge-Function-Schreibpfad mit echtem bytea
// wechseln.
// -----------------------------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i] as number);
  if (typeof btoa === 'function') return btoa(bin);
  // Node-Fallback (Tests)
  return Buffer.from(bin, 'binary').toString('base64');
}

function base64ToBytes(b64: string): Uint8Array {
  const bin =
    typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Postgrest serialisiert bytea als `\x<hex>`. Wenn wir base64-Strings
 * eingespielt haben, kommt der base64-String hex-kodiert zurück — wir
 * dekodieren hex → ASCII (= unser base64-String).
 *
 * Wenn der Wert KEIN `\x`-Prefix hat (z.B. Edge-Function lieferte nativen
 * base64), geben wir ihn unverändert zurück.
 */
function extractBase64FromPostgrestBytea(value: string): string {
  if (!value.startsWith('\\x')) return value;
  const hex = value.slice(2);
  let s = '';
  for (let i = 0; i < hex.length; i += 2) {
    s += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
  }
  return s;
}
